import { useState, useMemo, useCallback, Fragment } from "react";
import { Mui, MuiIcons } from "@osu/react-ui";
import { getApplDetailParams, humanizeEnum } from "../../util/functions";
import { QUEUE_STATUS_LABELS, DECISION_LABELS } from "../../util/constants";
import {
    useGetScholarsPreferencesQuery,
    useGetScholarsProgramsQuery,
    useGetScholarsApplicantQueueEntriesQuery,
    useAdjustScholarsPreferencesMutation,
    useApprovePreferenceChangeRequestMutation,
    useApproveDenyRequestsMutation,
} from "../../services/graphql/api";
import { useApplicantDetailsState } from "../hooks/useApplicantDetailsState";
import { STUDENT_REQUEST_ACTIONS, PROGRAMS } from "../../util/constants";

// Preference-count bounds (mirror the backend rule in scholars-validation.mjs:
// ranks 1–3 required, 4–6 optional — i.e. a valid set has 3 to 6 programs).
const MIN_PREFERENCES = 3;
const MAX_PREFERENCES = 6;

// has648 — the fixed scholarsOutcomeReason the has572 bulk admin ADMIT stamps on the Application row
// (mirrors ADMIN_RECOMMENDATION_REASON in api scholarsBulkActionService.mjs). Any other non-empty reason
// is one of the has572 bulk admin DENY scenario reasons.
const ADMIN_RECOMMENDATION_ADMIT_REASON = "Admin Recommendation";
// administratorDetails uses "-" as its default-dash placeholder for absent values.
const presentOrNull = (v) => (v && v !== "-" ? v : null);

// has648 — admit-path markers persisted on the queue entry as admitSource (mirrors SCHOLARS_ADMIT_SOURCE in
// api alternateAssignmentService.mjs).
export const ADMIT_SOURCE = Object.freeze({
    OPT_IN_LIST: "OPT_IN_LIST",
    ADMIN_RECOMMENDATION: "ADMIN_RECOMMENDATION",
});

/**
 * has648 — Which admit path created this program placement, if any: an Opt-In List admit or a has572
 * Advanced Search Administrative Recommendation admit.
 * @returns {"OPT_IN_LIST"|"ADMIN_RECOMMENDATION"|null}
 */
export function getAdmitSource(entry, adminDetails) {
    if (entry?.admitSource) return entry.admitSource;
    if (entry?.fromAlternateList !== true) return null;
    const isAdminRecAdmit = presentOrNull(adminDetails?.scholarsOutcomeReason) === ADMIN_RECOMMENDATION_ADMIT_REASON;
    const adminRecProgram = presentOrNull(adminDetails?.scholarsAdmittedSubProgramId)
        ?? presentOrNull(adminDetails?.scholarsActiveSubProgramId);
    return isAdminRecAdmit && adminRecProgram === entry.subProgramId
        ? ADMIT_SOURCE.ADMIN_RECOMMENDATION
        : ADMIT_SOURCE.OPT_IN_LIST;
}

/**
 * has648 — The has572 Administrative Recommendation DENY reason, or null. A deny is applicant-level (not
 * tied to a program), so it is shown as its own unranked row. Like Honors, it appears as soon as the admin
 * submits it from Advanced Search.
 */
export function getAdminRecommendationDenyReason(adminDetails) {
    const reason = presentOrNull(adminDetails?.scholarsOutcomeReason);
    return reason && reason !== ADMIN_RECOMMENDATION_ADMIT_REASON ? reason : null;
}

/**
 * has504 — Scholars Preferences section on the applicant details page.
 * Shows the ranked program preferences and allows admin editing with cascade awareness.
 */
export default function ScholarsPreferences() {
    const { applicantEmployeeId, term } = getApplDetailParams();
    const { data: preferences, isLoading, isError, refetch } = useGetScholarsPreferencesQuery(
        { applicantEmployeeId, term },
        { skip: !applicantEmployeeId }
    );
    const { data: programs } = useGetScholarsProgramsQuery();
    const { data: queueEntries } = useGetScholarsApplicantQueueEntriesQuery(
        { applicantEmployeeId, term },
        { skip: !applicantEmployeeId }
    );
    // Read alternateOptIn from the cached application details (administratorDetails blob).
    // Route-aware hook: the Scholars applicant page loads the getScholarsApplicationDetails cache,
    // not the Honors getApplicationDetails one — reading the wrong cache always yielded false.
    const { data: appDetails } = useApplicantDetailsState();
    const appAlternateOptIn = appDetails?.details?.administratorDetails?.alternateOptIn ?? false;

    const [editMode, setEditMode] = useState(false);
    const [proposedChangesMode, setProposedChangesMode] = useState(false);
    const [editRankings, setEditRankings] = useState([]);
    const [alternateOptIn, setAlternateOptIn] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(null);
    const [submitResult, setSubmitResult] = useState(null);
    // Per-program expanded state for the submitted-notes detail row (read-only view).
    const [expandedNotes, setExpandedNotes] = useState({});
    const toggleNotes = useCallback((subProgramId) => {
        setExpandedNotes(prev => ({ ...prev, [subProgramId]: !prev[subProgramId] }));
    }, []);

    const [adjustPreferences, { isLoading: isSaving }] = useAdjustScholarsPreferencesMutation();
    const [approvePreferenceChangeRequest, { isLoading: isApproving }] = useApprovePreferenceChangeRequestMutation();
    const [approveDenyRequests, { isLoading: isDeclining }] = useApproveDenyRequestsMutation();

    // Detect active preference change request
    const adminDetails = appDetails?.details?.administratorDetails;
    const hasActivePreferenceChangeRequest =
        adminDetails?.studentRequestIsActive === "Yes" &&
        adminDetails?.studentRequestType === STUDENT_REQUEST_ACTIONS.PREFERENCE_CHANGE.label;

    // Build a lookup from subProgramId -> label
    const programLabels = useMemo(() => (programs ?? []).reduce((acc, p) => {
        acc[p.subProgramId] = p.label || p.subProgramId;
        return acc;
    }, {}), [programs]);

    // Build a lookup from subProgramId -> isOpen (program open/closed state).
    // Admin side keeps ALL programs visible; this drives the color-coded open/closed marker.
    const programOpenMap = useMemo(() => (programs ?? []).reduce((acc, p) => {
        acc[p.subProgramId] = p.isOpen === true;
        return acc;
    }, {}), [programs]);

    // Build queue status lookup
    const queueStatusMap = useMemo(() => (queueEntries ?? []).reduce((acc, e) => {
        acc[e.subProgramId] = e;
        return acc;
    }, {}), [queueEntries]);

    const sorted = useMemo(() =>
        [...(preferences ?? [])].sort((a, b) => a.rank - b.rank),
        [preferences]
    );

    // has648 — read-only table rows. An Opt-In List or Administrative Recommendation admit can target a
    // program the applicant never ranked, so queue entries with no matching preference are appended as
    // unranked rows (and marked with their admit source). A has572 Administrative Recommendation DENY is
    // not program-specific, so it gets its own unranked row. This shows the current lineage only, not an
    // exhaustive history of every admin action.
    const historyAdminDetails = appDetails?.details?.administratorDetails;
    const adminDenyReason = getAdminRecommendationDenyReason(historyAdminDetails);
    const displayRows = useMemo(() => {
        const ranked = new Set(sorted.map(p => p.subProgramId));
        const unranked = (queueEntries ?? [])
            .filter(e => e.subProgramId && !ranked.has(e.subProgramId))
            .map(e => ({ subProgramId: e.subProgramId, rank: null, status: null, updatedDatetime: null }));
        const adminDenyRow = adminDenyReason ? [{ isAdminDenyRow: true, reason: adminDenyReason }] : [];
        return [...sorted, ...unranked, ...adminDenyRow];
    }, [sorted, queueEntries, adminDenyReason]);

    // has508 — live (pre-save) validation warnings for the admin preference editor.
    // Warn as soon as the edited set drops below the required minimum or exceeds the allowed maximum,
    // before the admin clicks Save (the backend still enforces these same bounds on save).
    const validationWarnings = useMemo(() => {
        const count = editRankings.length;
        const warnings = [];
        if (count > 0 && count < MIN_PREFERENCES) {
            warnings.push(
                `At least ${MIN_PREFERENCES} program preferences are required — you currently have ${count}. `
                + `Add ${MIN_PREFERENCES - count} more before saving.`
            );
        }
        if (count > MAX_PREFERENCES) {
            warnings.push(
                `A maximum of ${MAX_PREFERENCES} program preferences is allowed — you currently have ${count}. `
                + `Remove ${count - MAX_PREFERENCES} before saving.`
            );
        }
        return warnings;
    }, [editRankings]);
    const hasValidationErrors = validationWarnings.length > 0;

    const enterEditMode = useCallback(() => {
        setEditRankings(sorted.map(p => ({ subProgramId: p.subProgramId, rank: p.rank })));
        setAlternateOptIn(appAlternateOptIn);
        setSubmitResult(null);
        setEditMode(true);
        setProposedChangesMode(false);
    }, [sorted, appAlternateOptIn]);

    const enterProposedChangesMode = useCallback(() => {
        // Pre-populate with the proposed rankings from the student request
        try {
            const proposed = typeof adminDetails?.proposedRankings === "string"
                ? JSON.parse(adminDetails.proposedRankings)
                : adminDetails?.proposedRankings;
            if (Array.isArray(proposed) && proposed.length > 0) {
                setEditRankings(proposed.map((r, i) => ({ subProgramId: r.subProgramId, rank: r.rank ?? i + 1 })));
            } else {
                setEditRankings(sorted.map(p => ({ subProgramId: p.subProgramId, rank: p.rank })));
            }
        } catch {
            setEditRankings(sorted.map(p => ({ subProgramId: p.subProgramId, rank: p.rank })));
        }
        setAlternateOptIn(adminDetails?.proposedAlternateOptIn ?? appAlternateOptIn);
        setSubmitResult(null);
        setEditMode(true);
        setProposedChangesMode(true);
    }, [sorted, appAlternateOptIn, adminDetails]);

    const cancelEdit = () => {
        setEditMode(false);
        setProposedChangesMode(false);
        setEditRankings([]);
        setSubmitResult(null);
        setConfirmDialog(null);
    };

    const moveUp = (index) => {
        if (index === 0) return;
        setEditRankings(prev => {
            const next = [...prev];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            return next.map((r, i) => ({ ...r, rank: i + 1 }));
        });
    };

    const moveDown = (index) => {
        setEditRankings(prev => {
            if (index >= prev.length - 1) return prev;
            const next = [...prev];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            return next.map((r, i) => ({ ...r, rank: i + 1 }));
        });
    };

    const removeRank = (index) => {
        setEditRankings(prev =>
            prev.filter((_, i) => i !== index).map((r, i) => ({ ...r, rank: i + 1 }))
        );
    };

    const addProgram = (subProgramId) => {
        if (editRankings.some(r => r.subProgramId === subProgramId)) return;
        setEditRankings(prev => [...prev, { subProgramId, rank: prev.length + 1 }]);
    };

    const handleSave = async (confirmUpstream = false) => {
        setSubmitResult(null);
        try {
            const result = await adjustPreferences({
                applicantEmployeeId,
                term,
                rankings: editRankings,
                alternateOptIn,
                confirmUpstream,
            }).unwrap();

            if (!result.success && result.warning) {
                // Upstream scenario — need confirmation
                setConfirmDialog(result);
                return;
            }

            setSubmitResult({ success: true, message: `Preferences updated (${result.scenario}).${result.cascadeReset ? " Cascade has been reset." : ""}` });
            setEditMode(false);
            setConfirmDialog(null);
            refetch();
        } catch (err) {
            setConfirmDialog(null);
            setSubmitResult({ success: false, message: err?.data?.message || err?.message || JSON.stringify(err) || "Failed to update preferences." });
        }
    };

    const handleConfirmUpstream = () => handleSave(true);

    const handleApproveProposedChanges = async (confirmUpstream = false) => {
        setSubmitResult(null);
        try {
            const result = await approvePreferenceChangeRequest({
                input: {
                    applicantEmployeeId,
                    term,
                    rankings: editRankings,
                    alternateOptIn,
                    confirmUpstream,
                }
            }).unwrap();

            if (result?.status === "error" && result?.errorDetails?.length > 0) {
                // Could be a cascade warning — show confirmation dialog
                setConfirmDialog({ warning: result.errorDetails[0], isApproval: true });
                return;
            }

            setSubmitResult({ success: true, message: "Preference change request approved and applied successfully." });
            setEditMode(false);
            setProposedChangesMode(false);
            setConfirmDialog(null);
            refetch();
        } catch (err) {
            setConfirmDialog(null);
            setSubmitResult({ success: false, message: err?.data?.message || err?.message || JSON.stringify(err) || "Failed to approve preference change request." });
        }
    };

    const handleConfirmUpstreamApproval = () => handleApproveProposedChanges(true);

    const handleDeclineProposedChanges = async () => {
        setSubmitResult(null);
        try {
            await approveDenyRequests({
                applicantEmployeeIds: [applicantEmployeeId],
                programId: PROGRAMS.SCHOLARS.KEY,
                term,
                requestType: STUDENT_REQUEST_ACTIONS.PREFERENCE_CHANGE.KEY,
                isApproved: false,
            });
            setSubmitResult({ success: true, message: "Preference change request declined." });
            setEditMode(false);
            setProposedChangesMode(false);
            setConfirmDialog(null);
            refetch();
        } catch (err) {
            setSubmitResult({ success: false, message: err?.data?.message || err?.message || "Failed to decline preference change request." });
        }
    };

    if (isLoading) {
        return <Mui.Box sx={{ p: 2 }}><Mui.Skeleton variant="rectangular" height={120} /></Mui.Box>;
    }

    if (isError) {
        return <Mui.Alert severity="error" sx={{ m: 1 }}>Unable to load scholars preferences.</Mui.Alert>;
    }

    // Note: an empty preferences list is no longer a dead end. Admins can always enter edit mode
    // to set preferences from scratch, and can review proposed changes when a request is pending.
    // The empty-state messaging is handled inline in the read-only view below.

    // Available programs to add (those not already in the edit list)
    const availableToAdd = (programs ?? []).filter(p =>
        !editRankings.some(r => r.subProgramId === p.subProgramId)
    );

    return (
        <Mui.Box sx={{ p: 1 }}>
            {/* Result message */}
            {submitResult && (
                <Mui.Alert severity={submitResult.success ? "success" : "error"} sx={{ mb: 2 }} onClose={() => setSubmitResult(null)}>
                    {submitResult.message}
                </Mui.Alert>
            )}

            {!editMode ? (
                /* ─── Read-only view ─── */
                <>
                    {hasActivePreferenceChangeRequest && (
                        <Mui.Alert severity="info" sx={{ mb: 2 }} icon={<MuiIcons.SwapVert />}>
                            A preference change request is pending approval.
                            <Mui.Button
                                variant="outlined"
                                size="small"
                                sx={{ ml: 2 }}
                                onClick={enterProposedChangesMode}
                            >
                                Review Proposed Changes
                            </Mui.Button>
                        </Mui.Alert>
                    )}
                    {/* Hide the direct Edit Preferences action while a preference change request is pending —
                        the admin should act through "Review Proposed Changes" above, so a separate edit entry
                        point here is redundant. */}
                    {!hasActivePreferenceChangeRequest && (
                        <Mui.Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                            <Mui.Button variant="outlined" size="small" startIcon={<MuiIcons.Edit />} onClick={enterEditMode} disabled={appDetails?.status?.isApplicationFinalized}>
                                Edit Preferences
                            </Mui.Button>
                        </Mui.Box>
                    )}
                    {sorted.length === 0 && (
                        <Mui.Alert severity="info" sx={{ mb: 1 }}>
                            This applicant has not submitted any program preferences.
                            {hasActivePreferenceChangeRequest
                                ? " Use Review Proposed Changes above to set them."
                                : " Use Edit Preferences to set them on the applicant's behalf."}
                        </Mui.Alert>
                    )}
                    {displayRows.length > 0 && (
                    <Mui.Table size="small" aria-label="Scholars program preferences">
                        <Mui.TableHead>
                            <Mui.TableRow>
                                <Mui.TableCell sx={{ fontWeight: "bold", width: 48 }} />
                                <Mui.TableCell sx={{ fontWeight: "bold", width: 60 }}>Rank</Mui.TableCell>
                                <Mui.TableCell sx={{ fontWeight: "bold" }}>Scholars Program</Mui.TableCell>
                                <Mui.TableCell sx={{ fontWeight: "bold" }}>Preference Status</Mui.TableCell>
                                <Mui.TableCell sx={{ fontWeight: "bold" }}>Queue Status</Mui.TableCell>
                                <Mui.TableCell sx={{ fontWeight: "bold" }}>Decision</Mui.TableCell>
                                <Mui.TableCell sx={{ fontWeight: "bold" }}>Last Preference Update</Mui.TableCell>
                                <Mui.TableCell sx={{ fontWeight: "bold" }}>Last Update</Mui.TableCell>
                            </Mui.TableRow>
                        </Mui.TableHead>
                        <Mui.TableBody>
                            {displayRows.map((pref) => {
                                if (pref.isAdminDenyRow) {
                                    return <AdminRecommendationDenyRow key="admin-recommendation-deny" reason={pref.reason} />;
                                }
                                const admitSource = getAdmitSource(queueStatusMap[pref.subProgramId], historyAdminDetails);
                                const noteGroups = queueStatusMap[pref.subProgramId]?.administrativeNotes ?? [];
                                const isExpanded = !!expandedNotes[pref.subProgramId];
                                return (
                                <Fragment key={pref.subProgramId}>
                                <Mui.TableRow>
                                    <Mui.TableCell sx={{ width: 48 }}>
                                        <Mui.IconButton
                                            size="small"
                                            onClick={() => toggleNotes(pref.subProgramId)}
                                            aria-label={`${isExpanded ? "Hide" : "Show"} submitted notes for ${programLabels[pref.subProgramId] || pref.subProgramId}`}
                                            aria-expanded={isExpanded}
                                            aria-controls={`scholars-notes-${pref.subProgramId}`}
                                        >
                                            {isExpanded ? <MuiIcons.KeyboardArrowUp fontSize="small" /> : <MuiIcons.KeyboardArrowDown fontSize="small" />}
                                        </Mui.IconButton>
                                    </Mui.TableCell>
                                    <Mui.TableCell>{pref.rank ?? "—"}</Mui.TableCell>
                                    <Mui.TableCell>
                                        <Mui.Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                            <Mui.Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                                <span>{programLabels[pref.subProgramId] || pref.subProgramId}</span>
                                                <AdmitSourceChip admitSource={admitSource} />
                                            </Mui.Box>
                                            <ProgramStatusChip isOpen={programOpenMap[pref.subProgramId]} />
                                        </Mui.Box>
                                    </Mui.TableCell>
                                    <Mui.TableCell><StatusChip status={pref.status} /></Mui.TableCell>
                                    <Mui.TableCell><QueueStatusChip entry={queueStatusMap[pref.subProgramId]} /></Mui.TableCell>
                                    <Mui.TableCell>{humanizeEnum(queueStatusMap[pref.subProgramId]?.recommendation, DECISION_LABELS) || "—"}</Mui.TableCell>
                                    <Mui.TableCell>{pref.updatedDatetime ? new Date(pref.updatedDatetime).toLocaleString() : "—"}</Mui.TableCell>
                                    <Mui.TableCell>{queueStatusMap[pref.subProgramId]?.updatedDatetime ? new Date(queueStatusMap[pref.subProgramId].updatedDatetime).toLocaleString() : "—"}</Mui.TableCell>
                                </Mui.TableRow>
                                <Mui.TableRow key={`${pref.subProgramId}-notes`}>
                                    <Mui.TableCell sx={{ py: 0, borderBottom: isExpanded ? undefined : "none" }} colSpan={8}>
                                        <Mui.Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Mui.Box id={`scholars-notes-${pref.subProgramId}`} role="region" aria-label={`Submitted notes for ${programLabels[pref.subProgramId] || pref.subProgramId}`}>
                                                <SubmittedNotes noteGroups={noteGroups} />
                                            </Mui.Box>
                                        </Mui.Collapse>
                                    </Mui.TableCell>
                                </Mui.TableRow>
                                </Fragment>
                                );
                            })}
                        </Mui.TableBody>
                    </Mui.Table>
                    )}
                </>
            ) : (
                /* ─── Edit mode ─── */
                <>
                    {proposedChangesMode && (
                        <Mui.Alert severity="warning" sx={{ mb: 2 }} icon={<MuiIcons.SwapVert />}>
                            <strong>Proposed Changes</strong> — Review and adjust the proposed preference changes below before approving.
                        </Mui.Alert>
                    )}

                    {/* Prior preferences comparison (only in proposed changes mode) */}
                    {proposedChangesMode && sorted.length > 0 && (
                        <Mui.Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "grey.300" }}>
                            <Mui.Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, color: "text.secondary" }}>
                                Current Preferences → Proposed
                            </Mui.Typography>
                            {sorted.map((pref) => {
                                const proposedRank = editRankings.find(r => r.subProgramId === pref.subProgramId)?.rank;
                                const isRemoved = !proposedRank;
                                const isChanged = proposedRank && proposedRank !== pref.rank;
                                const label = programLabels[pref.subProgramId] || pref.subProgramId;
                                return (
                                    <Mui.Box key={pref.subProgramId} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                                        <Mui.Typography
                                            variant="body2"
                                            sx={{
                                                textDecoration: (isRemoved || isChanged) ? "line-through" : "none",
                                                color: isRemoved ? "error.main" : isChanged ? "text.secondary" : "text.primary",
                                                minWidth: 200,
                                            }}
                                        >
                                            {pref.rank}. {label}
                                        </Mui.Typography>
                                        {isChanged && (
                                            <>
                                                <MuiIcons.ArrowForward fontSize="small" sx={{ color: "primary.main" }} />
                                                <Mui.Typography variant="body2" sx={{ color: "primary.main", fontWeight: "bold" }}>
                                                    #{proposedRank}
                                                </Mui.Typography>
                                            </>
                                        )}
                                        {isRemoved && (
                                            <Mui.Chip label="Removed" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
                                        )}
                                    </Mui.Box>
                                );
                            })}
                            {/* Show newly added programs */}
                            {editRankings.filter(r => !sorted.some(p => p.subProgramId === r.subProgramId)).map(r => (
                                <Mui.Box key={r.subProgramId} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                                    <Mui.Typography variant="body2" sx={{ color: "success.main", fontWeight: "bold", minWidth: 200 }}>
                                        {r.rank}. {programLabels[r.subProgramId] || r.subProgramId}
                                    </Mui.Typography>
                                    <Mui.Chip label="New" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
                                </Mui.Box>
                            ))}
                        </Mui.Box>
                    )}
                    <Mui.Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Mui.Typography variant="subtitle2" color="text.secondary">
                            {proposedChangesMode
                                ? "Make any additional adjustments, then approve to apply the changes."
                                : "Reorder, add, or remove programs. Denied programs cannot be re-added."}
                        </Mui.Typography>
                        <Mui.Box sx={{ display: "flex", gap: 1 }}>
                            <Mui.Button variant="outlined" size="small" onClick={cancelEdit} disabled={isSaving || isApproving || isDeclining}>Cancel</Mui.Button>
                            {proposedChangesMode ? (
                                <>
                                    <Mui.Button variant="outlined" size="small" color="error" onClick={handleDeclineProposedChanges} disabled={isApproving || isDeclining}>
                                        {isDeclining ? "Declining…" : "Decline"}
                                    </Mui.Button>
                                    <Mui.Button variant="contained" size="small" color="success" onClick={() => handleApproveProposedChanges(false)} disabled={isApproving || isDeclining || editRankings.length === 0 || hasValidationErrors}>
                                        {isApproving ? "Approving…" : "Approve & Apply Changes"}
                                    </Mui.Button>
                                </>
                            ) : (
                                <Mui.Button variant="contained" size="small" onClick={() => handleSave(false)} disabled={isSaving || editRankings.length === 0 || hasValidationErrors}>
                                    {isSaving ? "Saving…" : "Save Preferences"}
                                </Mui.Button>
                            )}
                        </Mui.Box>
                    </Mui.Box>

                    {/* has508 — pre-save validation warnings (min 3 / max 6 preferences) */}
                    {validationWarnings.map((warning, i) => (
                        <Mui.Alert key={i} severity="warning" sx={{ mb: 2 }}>
                            {warning}
                        </Mui.Alert>
                    ))}

                    <Mui.Table size="small">
                        <Mui.TableHead>
                            <Mui.TableRow>
                                <Mui.TableCell sx={{ fontWeight: "bold", width: 60 }}>Rank</Mui.TableCell>
                                <Mui.TableCell sx={{ fontWeight: "bold" }}>Scholars Program</Mui.TableCell>
                                <Mui.TableCell sx={{ fontWeight: "bold" }}>Queue Status</Mui.TableCell>
                                <Mui.TableCell sx={{ fontWeight: "bold", width: 140 }}>Actions</Mui.TableCell>
                            </Mui.TableRow>
                        </Mui.TableHead>
                        <Mui.TableBody>
                            {editRankings.map((r, index) => (
                                <Mui.TableRow key={r.subProgramId}>
                                    <Mui.TableCell>{r.rank}</Mui.TableCell>
                                    <Mui.TableCell>
                                        <Mui.Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                            <span>{programLabels[r.subProgramId] || r.subProgramId}</span>
                                            <ProgramStatusChip isOpen={programOpenMap[r.subProgramId]} />
                                        </Mui.Box>
                                    </Mui.TableCell>
                                    <Mui.TableCell><QueueStatusChip entry={queueStatusMap[r.subProgramId]} /></Mui.TableCell>
                                    <Mui.TableCell>
                                        <Mui.IconButton size="small" onClick={() => moveUp(index)} disabled={index === 0} aria-label="Move up">
                                            <MuiIcons.ArrowUpward fontSize="small" />
                                        </Mui.IconButton>
                                        <Mui.IconButton size="small" onClick={() => moveDown(index)} disabled={index === editRankings.length - 1} aria-label="Move down">
                                            <MuiIcons.ArrowDownward fontSize="small" />
                                        </Mui.IconButton>
                                        <Mui.IconButton size="small" onClick={() => removeRank(index)} color="error" aria-label="Remove">
                                            <MuiIcons.Close fontSize="small" />
                                        </Mui.IconButton>
                                    </Mui.TableCell>
                                </Mui.TableRow>
                            ))}
                        </Mui.TableBody>
                    </Mui.Table>

                    {/* Add program selector */}
                    {availableToAdd.length > 0 && (
                        <Mui.Box sx={{ mt: 2 }}>
                            <Mui.FormControl size="small" sx={{ minWidth: 250 }}>
                                <Mui.InputLabel id="add-program-label">Add Scholars Program</Mui.InputLabel>
                                <Mui.Select
                                    labelId="add-program-label"
                                    value=""
                                    label="Add Scholars Program"
                                    onChange={(e) => addProgram(e.target.value)}
                                >
                                    {availableToAdd
                                        .filter(p => {
                                            // Don't allow adding programs that have been DENIED in the queue
                                            const entry = queueStatusMap[p.subProgramId];
                                            return !entry || entry.status !== "DENIED";
                                        })
                                        .map(p => (
                                            <Mui.MenuItem key={p.subProgramId} value={p.subProgramId}>
                                                <Mui.Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                                                    <ProgramStatusChip isOpen={p.isOpen === true} />
                                                    <span>{p.label || p.subProgramId}</span>
                                                </Mui.Box>
                                            </Mui.MenuItem>
                                        ))
                                    }
                                </Mui.Select>
                            </Mui.FormControl>
                        </Mui.Box>
                    )}

                    {/* Alternate opt-in toggle */}
                    <Mui.FormControlLabel
                        sx={{ mt: 2 }}
                        control={<Mui.Switch checked={alternateOptIn} onChange={(e) => setAlternateOptIn(e.target.checked)} />}
                        label="Alternate Assignment Opt-In"
                    />
                </>
            )}

            {/* Upstream confirmation dialog */}
            <Mui.Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
                <Mui.DialogTitle>Confirm Cascade Reset</Mui.DialogTitle>
                <Mui.DialogContent>
                    <Mui.Alert severity="warning" sx={{ mb: 2 }}>
                        {confirmDialog?.warning}
                    </Mui.Alert>
                    <Mui.Typography variant="body2">
                        This action will cancel the current review and re-evaluate the applicant starting from their highest-ranked available program. Programs that previously denied this applicant will remain denied.
                    </Mui.Typography>
                </Mui.DialogContent>
                <Mui.DialogActions>
                    <Mui.Button onClick={() => setConfirmDialog(null)} disabled={isSaving || isApproving}>Cancel</Mui.Button>
                    <Mui.Button
                        onClick={confirmDialog?.isApproval ? handleConfirmUpstreamApproval : handleConfirmUpstream}
                        variant="contained"
                        color="warning"
                        disabled={isSaving || isApproving}
                    >
                        {(isSaving || isApproving) ? "Processing…" : "Confirm & Reset Cascade"}
                    </Mui.Button>
                </Mui.DialogActions>
            </Mui.Dialog>
        </Mui.Box>
    );
}

// has648 — marks a program row created by a non-cascade admit path (mirrors the Honors History
// "(Admin Recommendation)" marker). Renders nothing for normal cascade placements.
function AdmitSourceChip({ admitSource }) {
    if (admitSource === ADMIT_SOURCE.OPT_IN_LIST) {
        return <Mui.Chip label="Opt-In Admit" size="small" color="info" variant="outlined" />;
    }
    if (admitSource === ADMIT_SOURCE.ADMIN_RECOMMENDATION) {
        return <Mui.Chip label="Admin Recommendation" size="small" color="primary" variant="outlined" />;
    }
    return null;
}

// has648 — unranked row for a has572 Administrative Recommendation DENY (applicant-level, no program).
// Same 8-column shape as the preference rows; no notes expander since there is no program review.
function AdminRecommendationDenyRow({ reason }) {
    return (
        <Mui.TableRow>
            <Mui.TableCell sx={{ width: 48 }} />
            <Mui.TableCell>—</Mui.TableCell>
            <Mui.TableCell>
                <Mui.Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
                    <AdmitSourceChip admitSource={ADMIT_SOURCE.ADMIN_RECOMMENDATION} />
                    <Mui.Typography variant="body2">Reason: {reason}</Mui.Typography>
                </Mui.Box>
            </Mui.TableCell>
            <Mui.TableCell>—</Mui.TableCell>
            <Mui.TableCell>—</Mui.TableCell>
            <Mui.TableCell>Deny</Mui.TableCell>
            <Mui.TableCell>—</Mui.TableCell>
            <Mui.TableCell>—</Mui.TableCell>
        </Mui.TableRow>
    );
}

function StatusChip({ status }) {
    if (!status) return "—";
    const colorMap = { ACTIVE: "success", ARCHIVED: "default" };
    return <Mui.Chip label={humanizeEnum(status)} size="small" color={colorMap[status] || "default"} variant="outlined" />;
}

// has504 — Submitted administrative notes for a single program, shown in the expandable detail row
// under its ranked preference. Renders one block per reviewer who submitted notes, grouped by the
// three note sections (Document, Evaluation, Others/Concerns). Empty sections are omitted.
// Accessibility: notes are exposed as a description list (section label -> notes) with each section's
// notes in a real <ul>, so screen readers convey the label/value relationship and item count. Meaningful
// text uses the primary color to meet WCAG AA contrast; secondary is reserved for the timestamp.
function SubmittedNotes({ noteGroups }) {
    if (!noteGroups || noteGroups.length === 0) {
        return (
            <Mui.Typography variant="body2" sx={{ py: 1.5, pl: 2 }}>
                No submitted notes
            </Mui.Typography>
        );
    }
    const SECTIONS = [
        { key: "notesDocument", label: "Document" },
        { key: "notesEvaluation", label: "Evaluation" },
        { key: "notesOthersConcerns", label: "Others / Concerns" },
    ];
    return (
        <Mui.Box sx={{ py: 1.5, pl: 2, pr: 1 }}>
            <Mui.Typography
                component="h3"
                variant="subtitle2"
                sx={{ fontWeight: "bold", display: "block", mb: 1 }}
            >
                Submitted Notes
            </Mui.Typography>
            {noteGroups.map((g, i) => {
                const reviewer = [g.reviewerFirstName, g.reviewerLastName].filter(Boolean).join(" ")
                    || g.reviewerEmployeeId || "Reviewer";
                const sections = SECTIONS.filter(s => Array.isArray(g[s.key]) && g[s.key].length > 0);
                const submitted = g.submissionDatetime ? new Date(g.submissionDatetime).toLocaleString() : null;
                return (
                    <Mui.Box
                        key={g.reviewerEmployeeId || i}
                        component="section"
                        aria-label={`Notes from ${reviewer}`}
                        sx={{
                            borderLeft: "3px solid",
                            borderColor: "divider",
                            pl: 2,
                            py: 0.5,
                            mb: i < noteGroups.length - 1 ? 2 : 0,
                        }}
                    >
                        <Mui.Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
                            <Mui.Typography component="h4" variant="body2" sx={{ fontWeight: 600 }}>{reviewer}</Mui.Typography>
                            {submitted && (
                                <Mui.Typography variant="caption" color="text.secondary">
                                    Submitted {submitted}
                                </Mui.Typography>
                            )}
                        </Mui.Box>
                        {sections.length === 0 ? (
                            <Mui.Typography variant="body2">No note selections.</Mui.Typography>
                        ) : (
                            <Mui.Box component="dl" sx={{ m: 0 }}>
                                {sections.map(s => (
                                    <Mui.Box
                                        key={s.key}
                                        sx={{ display: "flex", gap: 1.5, mb: 0.75, "&:last-of-type": { mb: 0 } }}
                                    >
                                        <Mui.Typography
                                            component="dt"
                                            variant="body2"
                                            sx={{ fontWeight: "bold", minWidth: 120, flexShrink: 0, pt: "2px" }}
                                        >
                                            {s.label}
                                        </Mui.Typography>
                                        <Mui.Box
                                            component="dd"
                                            sx={{ m: 0 }}
                                        >
                                            <Mui.Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
                                                {g[s.key].map((note, idx) => (
                                                    <Mui.Typography key={idx} component="li" variant="body2">{note}</Mui.Typography>
                                                ))}
                                            </Mui.Box>
                                        </Mui.Box>
                                    </Mui.Box>
                                ))}
                            </Mui.Box>
                        )}
                    </Mui.Box>
                );
            })}
        </Mui.Box>
    );
}

function QueueStatusChip({ entry }) {
    if (!entry) return <Mui.Typography variant="body2" color="text.secondary">—</Mui.Typography>;
    const colorMap = {
        QUEUED: "info",
        IN_REVIEW: "warning",
        RECOMMENDED: "secondary",
        ADMITTED: "success",
        DENIED: "error",
        SKIPPED_CLOSED: "default",
        SKIPPED_PDNY: "default",
        CANCELLED: "default",
    };
    return <Mui.Chip label={humanizeEnum(entry.status, QUEUE_STATUS_LABELS)} size="small" color={colorMap[entry.status] || "default"} variant="outlined" />;
}

// has-pref-open-closed — small solid open/closed status dot for the admin applicant-detail editor.
// All programs remain visible on the admin side; this dot lets the admin tell at a glance which
// programs are open vs closed. Matches the solid FiberManualRecord dot used in the app-bar program
// chip. Green = Open, Red = Closed.
function ProgramStatusChip({ isOpen }) {
    const color = isOpen ? "#2e7d32" : "#d32f2f"; // MUI success.dark / error.main
    return (
        <MuiIcons.FiberManualRecord
            fontSize="small"
            titleAccess={isOpen ? "Open" : "Closed"}
            aria-label={isOpen ? "Open" : "Closed"}
            style={{ color, verticalAlign: "middle", fontSize: "0.85rem" }}
        />
    );
}